import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { DoorOpen, Users } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { tableLabel } from '../lib/tableLabel';

/**
 * PostSettleTablePrompt
 * --------------------------------------------------------------
 * Owner request (May 31 2026): *"When we settle a tab we must see
 * the option if we want to make that table available or still
 * occupied."* — staff don't always want the table cleared at the
 * exact moment they settle the bill (customer may still be sipping
 * the last drink / waiting for an Uber / about to order again).
 *
 * Rendered immediately after a successful tab settle by both
 * `OrdersPage.js confirmSettleTab` and `BarPanel.jsx settleTabCash`.
 * Two actions:
 *   - **Mark Available** → PATCH /api/tables/{id}/status with
 *     status=available (uses the standard guard rail — by now the
 *     bill is paid + tab closed, so any other unpaid order on the
 *     same table will still surface a friendly 400 the staff can
 *     act on).
 *   - **Keep Occupied** → close the dialog, do nothing.
 *
 * Props:
 *   open         (bool): visibility
 *   table        ({id, number}|null): table to mutate; if null,
 *                  the dialog is suppressed (e.g. takeaway tab
 *                  settled — no table to flip)
 *   onClose      (fn): always called when the user dismisses
 *   onResolved   (fn|undefined): called AFTER table state has been
 *                  changed (or skipped). Used by callers to refetch.
 */
export default function PostSettleTablePrompt({ open, table, onClose, onResolved }) {
  const [busy, setBusy] = useState(false);
  if (!table) return null;

  const close = () => { setBusy(false); onClose(); };

  const markAvailable = async () => {
    setBusy(true);
    try {
      await api.patch(`/tables/${table.id}/status`, { status: 'available' });
      toast.success(`${tableLabel(table.number)} → available`);
      onResolved?.();
      close();
    } catch (err) {
      // Standard guard rail (open tab on table, unpaid sibling order)
      // bubbles up here. The detail is human-readable.
      toast.error(err.response?.data?.detail || 'Could not mark available', { duration: 6000 });
      setBusy(false);
    }
  };

  const keepOccupied = () => {
    toast.message(`${tableLabel(table.number)} stays occupied`);
    onResolved?.();
    close();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="bg-bg-secondary border border-white/10 max-w-md" data-testid="post-settle-table-prompt">
        <DialogHeader>
          <DialogTitle className="font-serif text-white text-lg">
            Tab settled — what about <span className="text-gold">{tableLabel(table.number)}</span>?
          </DialogTitle>
          <DialogDescription className="text-zinc-400 font-sans text-xs">
            Pick whether the table should free up now or stay occupied for the customer.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <Button
            onClick={markAvailable}
            disabled={busy}
            className="h-14 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/40 rounded-sm font-sans uppercase tracking-[0.15em] text-xs font-semibold flex flex-col items-center justify-center gap-1 disabled:opacity-50"
            data-testid="post-settle-mark-available"
          >
            <DoorOpen className="w-5 h-5" />
            <span>Mark Available</span>
          </Button>
          <Button
            onClick={keepOccupied}
            disabled={busy}
            className="h-14 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/40 rounded-sm font-sans uppercase tracking-[0.15em] text-xs font-semibold flex flex-col items-center justify-center gap-1 disabled:opacity-50"
            data-testid="post-settle-keep-occupied"
          >
            <Users className="w-5 h-5" />
            <span>Keep Occupied</span>
          </Button>
        </div>
        <p className="text-[10px] text-zinc-500 font-sans text-center pt-1">
          You can always change the status later from the Tables tab.
        </p>
      </DialogContent>
    </Dialog>
  );
}
